import { useState } from 'react'
import { useTranslation } from '../../locales/useTranslation'

const STATUS_COLORS = {
  in_behandeling: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  geaccepteerd: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  geweigerd: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  geannuleerd: { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
}

const STATUS_LABELS = {
  in_behandeling: 'In behandeling',
  geaccepteerd: 'Geaccepteerd ✅',
  geweigerd: 'Geweigerd',
  geannuleerd: 'Geannuleerd',
}

const RequestCard = ({ request, onRespond }) => {
  const { t } = useTranslation()
  const [response, setResponse] = useState('')
  const [showResponse, setShowResponse] = useState(false)
  const [respondingTo, setRespondingTo] = useState(null)
  const status = STATUS_COLORS[request.status] || STATUS_COLORS.in_behandeling
  const charger = request.chargers
  const isPending = request.status === 'in_behandeling'

  const handleRespond = (newStatus) => {
    onRespond(request.id, newStatus, response)
    setShowResponse(false)
    setResponse('')
    setRespondingTo(null)
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: 16,
      padding: 16,
      border: isPending
        ? '1.5px solid var(--green-mid)'
        : '1px solid var(--cream-dark)',
      boxShadow: isPending
        ? '0 2px 12px rgba(61,158,95,0.1)'
        : '0 2px 8px rgba(0,0,0,0.04)'
    }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 10
      }}>
        <div style={{ flex: 1, marginRight: 10 }}>
          <div style={{
            fontFamily: 'Fraunces, serif',
            fontSize: 15,
            fontWeight: 900,
            color: 'var(--ink)',
            marginBottom: 2
          }}>
            {charger?.name}
          </div>
          <div style={{ color: 'var(--ink-muted)', fontSize: 12 }}>
            📅 Aanvraag ontvangen
          </div>
        </div>
        <span style={{
          background: status.bg,
          color: status.color,
          border: `1px solid ${status.border}`,
          borderRadius: 20,
          padding: '3px 10px',
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}>
          {STATUS_LABELS[request.status]}
        </span>
      </div>

      {/* Booking details */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
        marginBottom: 10
      }}>
        {[
          { label: 'Datum', value: request.date },
          { label: 'Tijd', value: `${request.start_time?.slice(0, 5)} – ${request.end_time?.slice(0, 5)}` },
          { label: 'Duur', value: `${request.duration_hours}u` },
          { label: 'Geschat', value: `€${request.total_price_est}` },
        ].map(({ label, value }) => (
          <div key={label} style={{
            background: 'var(--cream)',
            borderRadius: 10,
            padding: '8px 12px'
          }}>
            <div style={{
              fontSize: 10,
              color: 'var(--ink-muted)',
              fontWeight: 600,
              marginBottom: 2
            }}>
              {label}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 700 }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Driver message */}
      {request.driver_message && (
        <div style={{
          background: 'var(--cream)',
          borderRadius: 10,
          padding: '8px 12px',
          marginBottom: 10,
          fontSize: 12,
          color: 'var(--ink-soft)',
          fontStyle: 'italic'
        }}>
          <span style={{ fontWeight: 700, fontStyle: 'normal', color: 'var(--ink-muted)' }}>
            Bericht:{' '}
          </span>
          "{request.driver_message}"
        </div>
      )}

      {/* Owner response shown after decision */}
      {request.owner_response && (
        <div style={{
          background: 'var(--green-pale)',
          border: '1px solid var(--green-mid)',
          borderRadius: 10,
          padding: '8px 12px',
          marginBottom: 10,
          fontSize: 12,
          color: 'var(--ink-soft)'
        }}>
          <span style={{ fontWeight: 700, color: 'var(--green)' }}>Jouw reactie: </span>
          {request.owner_response}
        </div>
      )}

      {/* Accept / Reject buttons - only for pending */}
      {isPending && (
        <>
          {showResponse ? (
            <div>
              <textarea
                value={response}
                onChange={e => setResponse(e.target.value)}
                placeholder={t('reactie_placeholder')}
                rows={2}
                style={{
                  width: '100%',
                  background: 'var(--cream)',
                  border: '1.5px solid var(--cream-dark)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  color: 'var(--ink)',
                  fontSize: 13,
                  resize: 'none',
                  fontFamily: 'DM Sans, sans-serif',
                  marginBottom: 10,
                  lineHeight: 1.5
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    setShowResponse(false)
                    setRespondingTo(null)
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: 10,
                    border: '1px solid var(--cream-dark)',
                    background: 'white',
                    color: 'var(--ink-muted)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontFamily: 'DM Sans, sans-serif'
                  }}>
                  {t('annuleren')}
                </button>
                {respondingTo === 'accept' ? (
                  <button
                    onClick={() => handleRespond('geaccepteerd')}
                    style={{
                      flex: 2,
                      padding: '10px',
                      borderRadius: 10,
                      border: 'none',
                      background: 'var(--green)',
                      color: 'white',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontFamily: 'DM Sans, sans-serif'
                    }}>
                    ✅ {t('accepteren')}
                  </button>
                ) : (
                  <button
                    onClick={() => handleRespond('geweigerd')}
                    style={{
                      flex: 2,
                      padding: '10px',
                      borderRadius: 10,
                      border: 'none',
                      background: 'var(--red)',
                      color: 'white',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 13,
                      fontFamily: 'DM Sans, sans-serif'
                    }}>
                    ❌ {t('weigeren')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => {
                  setRespondingTo('reject')
                  setShowResponse(true)
                }}
                style={{
                  flex: 1,
                  padding: '11px',
                  borderRadius: 12,
                  border: '1.5px solid #fca5a5',
                  background: 'transparent',
                  color: 'var(--red)',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'DM Sans, sans-serif'
                }}>
                ❌ {t('weigeren')}
              </button>
              <button
                onClick={() => {
                  setRespondingTo('accept')
                  setShowResponse(true)
                }}
                style={{
                  flex: 2,
                  padding: '11px',
                  borderRadius: 12,
                  border: 'none',
                  background: 'var(--green)',
                  color: 'white',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'DM Sans, sans-serif'
                }}>
                ✅ {t('accepteren')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default RequestCard
